import { State, Store, Action, StateContext, Selector, createSelector } from '@ngxs/store';
import { 
  SaveModule,
  SaveModuleThumbnails,
  SaveModuleSuccess,
  SaveModuleFailure,
  ResetModules,
  SetSaveModule,
  GetModulePrimaryCategories,
  GetModuleSecondaryCategories,
  DuplicateModuleSuccess,
  DuplicateModuleFailure} from './modules.actions';
import { SerializeModuleService } from 'src/app/edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { ModulesService } from '../modules.service';
import { ModulesSaveResultEnum } from '../models/modules-save-result.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ModulesThumbnailService } from '../modules-thumbnail.service';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { forkJoin } from 'rxjs';
import { take, finalize } from 'rxjs/operators';
import { SaveModuleModel } from '../models/save-module.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';
import { StoryService } from 'src/app/story/story.service';
import { SetStory, SetStoryId } from 'src/app/story/state/story.actions';
import { StoryState } from 'src/app/story/state/story.state';

interface ModulesStateModel {
  primaryCategoryOptions: string[];
  secondaryCategoryOptions: string[];
  moduleExists: boolean;
  saveModule: SaveModuleModel;
  storyType: StoryType;
  saving: boolean;
}

const initialTrim = new TrimConfiguration();
initialTrim.graph.hidden = true;

const initialState = {
  primaryCategoryOptions: [],
  secondaryCategoryOptions: [],
  moduleExists: false,
  saveModule: new SaveModuleModel(),
  storyType: StoryType.Module,
  saving: false
};

@State<ModulesStateModel>({
  name: 'modules',
  defaults: initialState
})
export class ModulesState {
  constructor(
    private store: Store,
    private serializeModuleService: SerializeModuleService,
    private modulesService: ModulesService,
    private modulesThumbnailService: ModulesThumbnailService,
    private storyService: StoryService
  ) { } 

  @Selector()
  static getSaveModule(state: ModulesStateModel) {
    return state.saveModule;
  }

  static getPrimaryCategoryOptions(type: 'read' | 'write') {
    return createSelector(
      [ModulesState],
      (state: ModulesStateModel) => {
        return (type === 'read') ? 
          state.primaryCategoryOptions : 
          state.primaryCategoryOptions.filter(value => value !== 'exports');
      }
    );
  }

  @Selector()
  static getSecondaryCategoryOptions(state: ModulesStateModel) {
    return state.secondaryCategoryOptions;
  }

  @Selector()
  static getSaving(state: ModulesStateModel) {
    return state.saving;
  }


  @Action(SaveModule)
  saveModule(context: StateContext<ModulesStateModel>, action: SaveModule) {
    context.patchState({ saving: true });

    // Prepare the save configurations
    const saveConfig = action.saveConfig;
    const trimConfig = action.trimConfig ? action.trimConfig : new TrimConfiguration();

    // Generate a new namespace map for the serialize object, included all child properties.
    // Then serialize the module.
    const namespaceMap = saveConfig.duplicate ? action.saveConfig.namespaceMap : {};
    
    // Pull the labels out of state and merge into the story before serialization
    const labels = this.store.selectSnapshot(StoryLabelsState.getLabels);
    const chapters = this.store.selectSnapshot(StoryChaptersState.getStoryChapters);
    this.storyService.storeLabelsInChapters(labels, chapters);
    const serialized = this.serializeModuleService.serialize(
      saveConfig.replace,
      namespaceMap,
      trimConfig,
      action.saveConfig.duplicate,
      this.store.selectSnapshot(StoryState.getStory),
      this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
    );

    // Save module file to the server.
    this.modulesService.saveModule(serialized.story, action.id)
      .pipe(
        finalize(() => context.patchState({ saving: false }))
      )
      .subscribe(
        (result: ModulesSaveResultEnum) => {
          if (result === ModulesSaveResultEnum.Success) {
            if (serialized) {
              this.store.dispatch(new SetStory(serialized.story));
            }
            this.store.dispatch(new SetStoryId(action.id));

            context.dispatch(new SaveModuleSuccess(action.id));

            // lazy save the thumbnails. 
            context.dispatch(new SaveModuleThumbnails(action.id, serialized, saveConfig));            
          } else {
            context.dispatch(new SaveModuleFailure(action.id));
          }
        }
      );

    // Duplicate module [working]
    if (saveConfig.duplicate) {
      const copyRequests$ = [];
      Object.entries(namespaceMap)
        .forEach(([sourceState, targetState]) => {
          copyRequests$.push(this.modulesService.copyState(sourceState, targetState));
        });

      forkJoin(copyRequests$)
        .subscribe(
          (success) => {
            context.dispatch(new DuplicateModuleSuccess(action.id));
          },
          (error) => {
            context.dispatch(new DuplicateModuleFailure(action.id));
          }
        );
    }
  }

  // Send thumbnail data to server through thumbnail services
  // # FIXME: Work with @Brandon to support copying of thumbnails from module to module
  @Action(SaveModuleThumbnails)
  saveModuleThumbnails(context: StateContext<ModulesStateModel>, action: SaveModuleThumbnails) {
    
    // const { serialized, moduleId, saveConfig } = context.getState();
    const thumbs = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
    
    // call service to save
    const indexId = action.serialized.story.chapters[0].id;
    this.modulesThumbnailService.saveStoryThumbnails({
      storyId: action.id,
      storyType: StoryType.Module,
      thumbnails: thumbs,
      chapterIdMap: action.serialized.chapterIdMap, 
      indexId: indexId,
      deleteUnused: action.saveConfig.replace 
    }).subscribe(
      (success) => {
      }
    );
  }

  @Action(SetSaveModule)
  setSaveModule(context: StateContext<ModulesStateModel>, action: SetSaveModule) {
    context.patchState({
      saveModule: {...action.module}
    });
  }

  @Action(GetModulePrimaryCategories)
  getModulePrimaryCategories(context: StateContext<ModulesStateModel>, action: GetModulePrimaryCategories) {
    this.modulesService.getPrimaryCategories()
      .subscribe((categories) => {
        context.patchState({
          primaryCategoryOptions: categories
        });
      });
  }

  @Action(GetModuleSecondaryCategories)
  getModuleSecondaryCategories(context: StateContext<ModulesStateModel>, action: GetModuleSecondaryCategories) {
    this.modulesService.getSecondaryCategories(action.primaryCategory)
      .subscribe((categories) => {
        context.patchState({
          secondaryCategoryOptions: categories
        });
      });
  }

  // # TODO: Wire up full state reset, not just patchState
  @Action(ResetModules)
  resetModules(context: StateContext<ModulesStateModel>, action: ResetModules) {
    forkJoin(this.modulesService.getPrimaryCategories(), this.modulesService.getSecondaryCategories(''))
      .pipe(
        take(1)
      )
      .subscribe(([primaryCategories, secondaryCategories]) => {
        context.patchState({
          primaryCategoryOptions: primaryCategories,
          secondaryCategoryOptions: secondaryCategories
        });
      });
  }
}
